import { format } from 'date-fns'
import type { FraudDecision, FraudMode, FraudOutcome, FraudUserSummary } from '@/lib/api/fraud-types'

export const PILL = 'px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap'
export const EMPTY = '—'

export function moneyFormatter(currency?: string | null): (value: number | string | null | undefined) => string {
  const code = (currency || 'CAD').toUpperCase()
  try {
    const fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: code })
    return (value) => fmt.format(Number(value ?? 0) || 0)
  } catch {
    return (value) => `${code} ${(Number(value ?? 0) || 0).toFixed(2)}`
  }
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : format(date, 'MMM d, h:mm a')
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return EMPTY
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

export function userDisplayName(user: Pick<FraudUserSummary, 'first_name' | 'last_name' | 'email' | 'id'> | null | undefined): string {
  if (!user) return 'Unknown'
  const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
  return name || user.email || `User #${user.id}`
}

export const DECISION_META: Record<FraudDecision, { label: string; chip: string }> = {
  allow: { label: 'Allow', chip: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
  step_up: { label: '3DS step-up', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400' },
  decline: { label: 'Decline', chip: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' },
  blocked: { label: 'Blocked', chip: 'bg-neutral-800 text-white dark:bg-neutral-200 dark:text-neutral-900' },
}

export const OUTCOME_META: Record<FraudOutcome, { label: string; chip: string }> = {
  pending: { label: 'Pending', chip: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300' },
  succeeded: { label: 'Charged', chip: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' },
  declined: { label: 'Card declined', chip: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' },
  step_up_pending: { label: 'Awaiting 3DS', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400' },
  refused: { label: 'Refused', chip: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' },
  error: { label: 'Provider error', chip: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300' },
}

export const MODE_META: Record<FraudMode, { label: string; chip: string; description: string }> = {
  shadow: {
    label: 'Shadow',
    chip: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200',
    description: 'Every card charge is scored and recorded. Nothing is challenged or refused; blocks still apply.',
  },
  declines: {
    label: 'Declines only',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
    description: 'Hard rules refuse charges (chargeback history, daily cap, transfer lock). No 3-D Secure challenge.',
  },
  enforce: {
    label: 'Enforce',
    chip: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    description: 'Refusals plus a 3-D Secure challenge on the next charge when the policy asks for one.',
  },
}

/**
 * Human names for every signal `fraud.services` emits (features.evaluate and
 * risk.py). An unknown code falls back to Title Case, so a new backend
 * signal reads as "Reload Count" rather than `reload_count`.
 */
const SIGNAL_LABELS: Record<string, string> = {
  // Policy step-up rules
  reload_count: 'Reload count',
  spend_threshold: 'Spend threshold',
  single_amount: 'Single charge amount',
  velocity: 'Charge velocity',
  distinct_cards: 'Distinct cards',
  daily_cap: 'Daily cap',
  // Evidence
  prior_chargeback: 'Prior chargeback',
  card_shared_with_flagged_account: 'Card on a flagged account',
  card_shared_across_accounts: 'Card shared across accounts',
  issuer_fraud_decline: 'Issuer fraud decline',
  risk_rule_decline: 'Risk-rule decline',
  fingerprint_velocity: 'Card charged across accounts',
  card_testing: 'Card testing',
  recent_decline: 'Recent decline',
  retrying_after_soft_decline: 'Retrying after a soft decline',
  many_new_cards: 'Many new cards',
  many_cards_on_account: 'Many cards on account',
  new_account: 'New account',
  transfers_out: 'Transfers out',
  unknown_card: 'Unknown card',
  card_not_3ds_verified: 'Card not 3DS verified',
  card_verification_failed: 'Card verification failed',
  prepaid_card: 'Prepaid card',
  // Not scored: trust, blocks and how the charge reached us
  trusted: 'Trusted',
  active_block: 'Active block',
  blocked_card: 'Blocked card',
  wallet_token: 'Wallet token',
  verified_card_challenged: 'Verified card challenged',
  provider_without_3ds: 'Provider without 3DS',
  evervault_disabled: 'Evervault disabled',
  evervault_unconfigured: 'Evervault not configured',
  policy_unavailable: 'Policy unavailable',
  scoring_unavailable: 'Scoring unavailable',
}

/** What was being decided: the assessment's kind as a phrase. */
const KIND_LABELS: Record<string, string> = {
  topup: 'App top-up',
  staff_topup: 'Staff top-up',
  auto_topup: 'Auto top-up',
  offline_topup: 'Offline top-up',
  direct_pay: 'Direct pay',
  pos_direct_pay: 'POS direct pay',
  transfer: 'Transfer',
  card_add: 'Card added',
}

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind.replace(/_/g, ' ')
}

/** A provider failure code as a phrase: "FRAUD_DETECTED" → "Fraud detected". */
export function failureLabel(code?: string | null): string {
  const words = (code ?? '').toLowerCase().split('_').filter(Boolean)
  if (!words.length) return ''
  return words.map((word, i) => (i === 0 ? word[0].toUpperCase() + word.slice(1) : word)).join(' ')
}

/** "reload_count" → "Reload count"; an unmapped code → "Reload Count". */
export function signalLabel(code: string): string {
  if (SIGNAL_LABELS[code]) return SIGNAL_LABELS[code]
  return code.split('_').filter(Boolean).map((word) => word[0].toUpperCase() + word.slice(1)).join(' ') || code
}

export const TH = 'text-left text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 py-2 px-3'
export const TD = 'py-2 px-3 text-sm text-neutral-700 dark:text-neutral-300 align-top'
export const ROW = 'border-b border-neutral-100 dark:border-neutral-800 last:border-0'

/** "Apple Pay" / "Google Pay" for a wallet instrument, else nothing. */
export function walletName(wallet?: string | null): string {
  return wallet === 'APPLE_PAY' ? 'Apple Pay' : wallet === 'GOOGLE_PAY' ? 'Google Pay' : ''
}

/** "Apple Pay · " / "Google Pay · " prefix for a wallet instrument, else nothing. */
export function walletLabel(wallet?: string | null): string {
  const name = walletName(wallet)
  return name ? `${name} · ` : ''
}

/** "3 charges" for a wallet card that stands for several per-charge instruments. */
export function instrumentCount(n?: number): string {
  const count = n ?? 1
  return `${count} charge${count === 1 ? '' : 's'}`
}
